import Close from '@betfan/icons/linear/EssentionalUI/Close.svg?react';
import type { Player as PlayerType } from '@lottiefiles/react-lottie-player';
import DislikeAnimation from 'assets/animations/Dislike.json';
import LikeAnimation from 'assets/animations/Like.json';
import LoveAnimation from 'assets/animations/Love.json';
import cn from 'classnames';
import { t } from 'i18next';
import React, { lazy, Suspense, useRef, useState } from 'react';
import { BetslipEvents, setDataLayer } from 'shared/constants/datalayer';
import { isMobile } from '../../../../shared/util/utility';

const Player = lazy(() => import('@lottiefiles/react-lottie-player').then((module) => ({ default: module.Player })));

interface RatingBarProps {
	hideRatingBar: () => void;
}

type RatingValue = 'bad' | 'good' | 'super';

interface RatingOptionProps {
	value: RatingValue;
	rated: () => void;
}

const RatingOption = (props: RatingOptionProps) => {
	const playerRef = useRef<PlayerType>(null);

	const handleRating = () => {
		playerRef.current?.play();
		localStorage.setItem('quickShotRatingSubmitted', 'true');
		setDataLayer(BetslipEvents.BETBUILDER_RATING(props.value));
		setTimeout(() => props.rated(), 1500);
	};

	const getAnimation = () => {
		switch (props.value) {
			case 'bad':
				return DislikeAnimation;
			case 'good':
				return LikeAnimation;
			case 'super':
				return LoveAnimation;
		}
	};

	return (
		<div
			className={cn('h-full flex items-center justify-center gap-1 cursor-pointer', { 'flex-col w-[54px]': isMobile(), 'px-2': !isMobile() })}
			onClick={handleRating}
		>
			<div className="size-5 relative">
				<Suspense fallback={<></>}>
					{/* The icon should be 20x20 but the whole animation is 52x52 */}
					<Player
						ref={playerRef}
						className="absolute size-[52px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
						src={getAnimation()}
						autoplay={false}
						loop={false}
					/>
				</Suspense>
			</div>

			<span className={`flex text-label-2xs justify-center items-center`}>{t('quickShot_ratingBar_' + props.value)}</span>
		</div>
	);
};

const RatingBar: React.FC<RatingBarProps> = ({ hideRatingBar }) => {
	const [isRatingClicked, setIsRatingClicked] = useState(false);

	const handleRated = () => {
		setIsRatingClicked(true);

		window.setTimeout(() => hideRatingBar(), 5000);
	};

	return (
		<div className={cn('w-full bg-white dark:bg-dark-200 mt-3 items-center rounded-4', isMobile() ? 'h-[54px]' : 'h-12')}>
			{isRatingClicked ? (
				<>
					<div className={cn('h-full grid items-center justify-between pl-2', isMobile() ? 'grid-cols-2' : 'grid-cols-3')}>
						{/* Dummy column to push the text to the center */}
						{!isMobile() && <div />}
						<div className={cn('text-paragraph-xs', { 'flex justify-center': !isMobile() })}>{t('quickShot_ratingBar_thankYou')}</div>
						<div className="flex justify-end">
							<button onClick={hideRatingBar} className="pr-2 pl-4 h-full">
								<Close className="w-5 h-5" />
							</button>
						</div>
					</div>
				</>
			) : (
				<div className="flex items-center justify-center h-full">
					<div className={cn('flex justify-between items-center px-2 h-full', isMobile() ? 'w-full gap-2' : 'gap-4')}>
						<div className={`flex items-center text-paragraph-xs`}>{t('quickShot_ratingBar_chooseRating')}</div>
						<div className="h-full flex gap-1">
							<RatingOption value="bad" rated={handleRated} />
							<RatingOption value="good" rated={handleRated} />
							<RatingOption value="super" rated={handleRated} />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default RatingBar;
